import React from 'react';
import { styled, Theme } from '@mui/material/styles';
import TrimbleMaps, { LngLatBoundsLike } from '@trimblemaps/trimblemaps-js';
import { AllGeoJSON, bbox } from '@turf/turf';

import { NetworkArea } from '../../interfaces/services/areas';
import { MapLayers, MapSources } from '../../utils/maps/enums';
import {
    fitMapToBounds,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    trimbleMapsPopup,
} from '../../utils/maps/mapUtils';
import { addBalanceAreasCenterLayer } from '../../utils/maps/layers/balanceAreas';
import { BalanceAreasMapSourceProperties, addBalanceAreasSource } from '../../utils/maps/sources/balanceAreas';
import MapSpinner from '../Loaders/MapSpinner';

import '../../assets/CSS/trimbleMaps.css';

const classesPrefix = 'balanceMap';

const classes = {
    map: `${classesPrefix}-map`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.map}`]: {
            height: '100%',
            minHeight: '300px',
        },
    };
});

interface BalanceMapProps {
    /** Indicator to show loading spinner while data is fetching. */
    isFetchingData: boolean;
    /** Areas to add to the map */
    balanceAreas: NetworkArea[];
    /** MUI theme for styling the map layers */
    theme: Theme;
    /** Indicator to trigger a map resize function. */
    resize?: boolean;
}

interface BalanceMapState {
    mapStyleLoaded: boolean;
}

let map: TrimbleMaps.Map | null = null;
const mapContainer = React.createRef<HTMLDivElement>();

class BalanceMap extends React.Component<BalanceMapProps, BalanceMapState> {

    constructor(props: BalanceMapProps) {
        super(props);

        TrimbleMaps.APIKey = '173580F5C6F48F44B5006F78909958C1';

        this.state = {
            mapStyleLoaded: false,
        };
    }

    componentDidMount(): void {
        const latitude = 39.828347;
        const longitude = -98.579488;

        if (!mapContainer.current) { return; }

        map = new TrimbleMaps.Map({
            container: mapContainer.current, // Ties to an HTML DOM element
            center: [longitude, latitude], // Sets initial center of map
            style: TrimbleMaps.Common.Style.DATADARK, // Sets initial map style
            minZoom: 1.55, // To stop the zoom from showing multiple worlds
            zoom: 3, // Sets initial zoom level
        }).addControl(
            // Adds the zoom in/out and compass buttons
            new TrimbleMaps.NavigationControl()
        ).addControl(
            new TrimbleMaps.FullscreenControl()
        );

        map.setRegion(TrimbleMaps.Common.Region.WW);

        // README: The event `style.load` is no longer a public event of mapbox. We will need to use some combination of using `styledata` and `map.isStyleLoaded()` at some point.
        // However, these do not work the same and there are several open issues with mapbox that have yet to be resolved, such as this one:
        // https://github.com/mapbox/mapbox-gl-js/issues/9779
        // Once a map style is loaded, add any layers that were on back to the map
        map.on('style.load', (): void => {
            // The style.load event can fire after the component has been unmounted.
            if (map === null) {
                return;
            }

            this.removeBalanceAreasEventHandlers();
            this.addBalanceAreasToMap();

            // HACK: trigger a resize function to make map take up full width
            map.resize();
            this.setState({ mapStyleLoaded: true });
        });
    }

    componentDidUpdate(prevProps: BalanceMapProps): void {
        // if the map is null, hold any CDU updates
        if (map === null) {
            return;
        }

        if (this.state.mapStyleLoaded === false) {
            return;
        }

        // add balance area markers to map if props have updated
        if (prevProps.balanceAreas !== this.props.balanceAreas) {
            this.removeBalanceAreasEventHandlers();
            this.addBalanceAreasToMap();
        }

        // if resize prop is passed and is true force map to resize.
        if (this.props.resize === true) {
            map.resize();
        }
    }

    componentWillUnmount(): void {
        this.removeBalanceAreasEventHandlers();
    }

    addBalanceAreasToMap = (): void => {
        if (map) {
            // add map source
            const balanceAreasSource = addBalanceAreasSource(map, this.props.balanceAreas || [], this.props.theme);

            // add area centers
            addBalanceAreasCenterLayer({
                map,
                mapSource: MapSources.balanceAreasSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.balanceAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.balanceAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            const { data } = balanceAreasSource;
            if ((data as GeoJSON.FeatureCollection<GeoJSON.Point, BalanceAreasMapSourceProperties>).features.length > 0) {
                const boundingBox = bbox(data as AllGeoJSON);
                fitMapToBounds({
                    map,
                    bounds: boundingBox as LngLatBoundsLike,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    removeBalanceAreasEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.balanceAreasCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.balanceAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.balanceAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }
        }
    };

    render(): React.JSX.Element {
        return (
            <StyledDiv
                id='trimble-map-container'
                ref={mapContainer}
                className={classes.map}
                data-qa='balanceMap-container'
            >
                {
                    this.props.isFetchingData &&
                    <MapSpinner />
                }
            </StyledDiv>
        );
    }

}

export default BalanceMap;
